<template>
  <div>
    <slide-over-panel   v-model:show="showMeterSelector"
                        width="sm:max-w-2xl md:max-w-2xl lg:max-w-3xl xl:max-w-5xl">
      <template   #content>
        <FoodBatchData
          :isOpen="showMeterSelector !== null"
          :company-id="company.getCompanyId()"
          :meter-id="selectedMeter"
          :food-batch-id="foodBatch.id"
          :food-batch-created-at="foodBatch.created_at"
        />
      </template>
    </slide-over-panel>
  </div>
  <div>
    <!-- Header Section -->
    <div class="flex items-center justify-between">
      <h1 class="text-3xl font-bold text-gray-900">Food Batch Details</h1>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
      <span class="block sm:inline">{{ successMessage }}</span>
    </div>

    <!-- Basic Information Section -->
    <div class="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
      <div class="px-4 py-5 sm:px-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900">Basic Information</h3>
        <p class="mt-1 max-w-2xl text-sm text-gray-500">Details of the Food Batch.</p>
      </div>
      <div class="border-t border-gray-200">
        <dl>
          <!-- Food -->
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Food</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ foodBatch.food }}</dd>
          </div>
          <!-- Action -->
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Action</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ foodBatch.action }}</dd>
          </div>
          <!-- Lot # -->
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Lot #</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ foodBatch.lot }}</dd>
          </div>
          <!-- Zone Name -->
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Zone</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ foodBatch.zone_name }}</dd>
          </div>
          <!-- Originating User -->
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Originating User</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ foodBatch.originating_user.display_name }}</dd>
          </div>
        </dl>
      </div>
    </div>

    <!-- Status Information Section -->
    <div class="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
      <div class="px-4 py-5 sm:px-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900">Status Information</h3>
      </div>
      <div class="border-t border-gray-200">
        <dl>
          <!-- Status -->
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Status</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ foodBatch.status }}</dd>
          </div>
          <!-- Created At -->
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Created At</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ formattedCreatedAt }}</dd>
          </div>
          <!-- Closed At -->
          <div v-if="foodBatch.closed_at" class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium text-gray-500">Closed At</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ formattedClosedAt }}</dd>
          </div>
        </dl>
        <!-- Close Batch Button -->
        <div v-if="foodBatch.status === 'active'" class="px-4 py-4 sm:px-6">
          <form :action="this.company.buildCompanyRoute(`food-batches/${foodBatch.id}`)" method="POST">
            <!-- CSRF Token -->
            <input type="hidden" name="_token" :value="csrf">
            <!-- Method Spoofing to DELETE -->
            <input type="hidden" name="_method" value="DELETE">
            <button
              type="submit"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none"
            >
              Close Batch
            </button>
          </form>
        </div>
      </div>
    </div>


    <!-- Food Batch Data Section -->
    <div class="mt-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg leading-6 font-medium text-gray-900">Food Batch Data</h3>
      </div>
<!--      Add a select2 with the meter prop data-->
      <select2    id="selected_meters[]"
                  :multiple="false"
                  :options="meters"
                  v-model:value="selectedMeter"
                  :allow-clear="true"
                  :close-on-select="true"
                  placeholder="Select a Point to add data to this Food Batch"
      ></select2>
<!--      Add a button that's enabled if selectMeters is enabled, it should toggle a slideover value -->
      <button v-if="selectedMeter" @click="showMeterSelector = true" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700">
        Add Data
      </button>

      <div v-if="foodBatch.food_batch_data" class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Temperature (F)</th>
            </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="data in sortedFoodBatchData" :key="data.id">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ data.time_readable }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ data.value }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500">No Food Batch Data available.</p>
    </div>

    <div class="mt-8">
      <!-- Corrective Actions Header -->
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg leading-6 font-medium text-gray-900">Corrective Actions</h3>
      </div>

      <!-- Display Existing Corrective Actions -->
      <div v-if="foodBatch.corrective_actions.length" class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <ul class="divide-y divide-gray-200">
            <li v-for="(action, index) in foodBatch.corrective_actions" :key="index" class="py-4">
              <div class="flex space-x-3">
                <div class="flex-1 space-y-1">
                  <h4 class="text-sm font-medium text-gray-900">{{ action.user_display_name }} on {{action.created_at_readable}}</h4>
                  <p class="text-sm text-gray-500">{{ action.comment }}</p>
                  <div v-if="action.is_overridden" class="text-sm text-red-500">This batch's status was overridden.</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500">No Corrective Actions available.</p>

      <!-- Form to Add Corrective Action -->
      <div class="mt-6">
        <form :action="patchRoute" method="POST">
          <!-- CSRF Token -->
          <input type="hidden" name="_token" :value="csrf">
          <!-- Spoof HTTP Method to PATCH -->
          <input type="hidden" name="_method" value="PATCH">

          <!-- Corrective Action Textarea -->
          <div class="mb-4">
            <label for="corrective_action" class="block text-sm font-medium text-gray-700">
              Add a Corrective Action:
            </label>
            <textarea
              id="corrective_action"
              name="corrective_action"
              rows="3"
              class="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              required
            ></textarea>
          </div>

          <!-- Override Checkbox -->
          <div v-if="foodBatch.status !== 'overridden' && foodBatch.status !== 'complete'"
            class="flex items-center mb-4">
            <input
              id="is_overridden"
              name="is_overridden"
              type="checkbox"
              value="1"
              class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label for="is_overridden" class="ml-2 block text-sm text-gray-900">
              Mark this batch's status as Overridden
            </label>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
          >
            Submit
          </button>
        </form>
      </div>
    </div>

    <!-- Meter Selection Section -->
  </div>
</template>

<script>
import Select2 from "../elements/select2.vue";
import SlideOverPanel from "../elements/SlideOverPanel.vue";
import FoodBatchData from "./foodBatchData.vue";
import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";

export default {
  name: 'FoodBatchShow',
  mixins: [SonicuCoreMixin],
  components: {
    FoodBatchData,
    SlideOverPanel,
    Select2,
  },
  props: {
    foodBatch: {
      type: Object,
      required: true
    },
    meters: {
      type: Object,
      required: true
    },
    successMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedMeter: null,
      showMeterSelector: false,
    };
  },
  computed: {
    formattedCreatedAt() {
      return new Date(this.foodBatch.created_at).toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    },
    formattedClosedAt() {
      if (!this.foodBatch.closed_at) return '';
      return new Date(this.foodBatch.closed_at).toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    },
    sortedFoodBatchData() {
      return this.foodBatch.food_batch_data.slice().sort((a, b) => {
        return new Date(a.time_readable) - new Date(b.time_readable);
      });
    },
    patchRoute(){
      let endRoute = `food-batches/${this.foodBatch.id}`;
      return this.company.buildCompanyRoute(endRoute)
    }
  }
};
</script>

<style scoped>
/* Optional: Add component-specific styles here */
</style>
